import React, { useEffect, useState } from 'react';
import { ClientDetailsTableContext, IClientDetailsTableContext } from './ClientDetailsTableContext';
import { useAsyncData } from '@shared/react';
import { getUnassignedClient } from '../../api';
import useStore from '@widgets/UserManagementTable/lib/store/assign-client-store';
import { usePagination2 } from '@features/Pagination/usePagination';
import ClientRefreshStore from '@widgets/UserManagementTable/lib/store/clients-refresh-store';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

interface ClientDetailsTableProviderProps {
    userId: number;
}

export const ClientDetailsTableProvider: React.FC<React.PropsWithChildren<ClientDetailsTableProviderProps>> = ({
    userId,
    children,
}) => {
    const [ClientDetailsIdentifier, setClientDetailsIdentifier] = useState<string>('');
    const [cusip, setCusip] = useState<string>('');
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(['', '']);
    const [sortFieldName, setSortFieldName] = useState('id');
    const [userType, setUserType] = useState<string | null>(null);
    const clientAssigned = useStore();
    const refreshAccounts = ClientRefreshStore();
    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();

    const {
        data: ClientDetailsBody,
        handleRefresh: accountsRefresh,
        loading: clientLoading,
    } = useAsyncData(
        async () => {
            const { items, totalResults } = await getUnassignedClient(
                { page: page, pageSize, sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}` },
                userId,
            );

            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [pageSize, page, ClientDetailsIdentifier, cusip, dateRange, userType],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            return await Promise.all(
                ClientDetailsBody.map(async (item) => {
                    return { item };
                }),
            );
        },
        [],
        [ClientDetailsBody],
    );

    const value: IClientDetailsTableContext = {
        dateRange,
        ClientDetailsBody,
        logoLoading,
        paginationOptions,
        sortFieldName,
        desc,
        userType,
        clientLoading,
        setClientDetailsIdentifier,
        setCusip,
        setDateRange,
        setUserType,
    };

    useEffect(() => {
        if (clientAssigned) {
            accountsRefresh();
        }
    }, [clientAssigned]);

    useEffect(() => {
        if (refreshAccounts) {
            accountsRefresh();
        }
    }, [refreshAccounts]);

    return <ClientDetailsTableContext.Provider value={value}>{children}</ClientDetailsTableContext.Provider>;
};
